import { useEffect, useState } from "react";
import type { PatientFlagSummary } from "@dentacore/shared-types";

import { getPatient } from "../api/patients";
import styles from "./critical-alerts.module.css";

/**
 * **A patient's critical alerts, wherever a booking is made** (PP-D1, audit
 * F-12) — the booking form, where a course now starts too, and a WhatsApp
 * request's approval card, since approving one is booking it.
 *
 * **Shown, never a refusal**: no tick to confirm and nothing blocked. **Critical
 * only**, so "prefers mornings" stays on the file header. A rule and a word as
 * well as a colour, as the appointment panel's band has, because colour alone
 * fails the colour-vision constraint.
 *
 * Reads the file once a patient is chosen; draws nothing while it reads, for a
 * patient with none, or if the read fails (the booking is never held up by it).
 */
export function CriticalAlerts({
  patientId,
  testId = "critical-alerts",
}: {
  patientId: string | null;
  testId?: string;
}): React.JSX.Element | null {
  const [critical, setCritical] = useState<readonly PatientFlagSummary[]>([]);

  useEffect(() => {
    setCritical([]);
    if (patientId === null) return;
    const controller = new AbortController();
    getPatient(patientId, controller.signal)
      .then((file) => setCritical(file.flags.filter((flag) => flag.severity === "CRITICAL")))
      .catch(() => {
        /* Nothing drawn; the header and the appointment panel still carry them. */
      });
    return () => controller.abort();
  }, [patientId]);

  if (critical.length === 0) return null;

  return (
    <div className={styles.band} role="note" data-testid={testId}>
      <span className={styles.label}>Critical</span>
      <span className={styles.text}>
        {critical
          .map((flag) => (flag.detail === null ? flag.label : `${flag.label} — ${flag.detail}`))
          .join(" · ")}
      </span>
    </div>
  );
}
